import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useQuickscan } from '@/contexts/QuickscanContext';
import { ArrowRight, ArrowLeft, Zap, SkipForward } from 'lucide-react';
import { Logo } from '../../Logo';

export const NewEquipmentSubScreen = () => {
  const { data, updateExpansionDetails, nextStep, prevStep, skipSubScreen } = useQuickscan();
  
  const [equipmentData, setEquipmentData] = useState({
    equipmentType: data.expansionDetails.equipment?.equipmentType || '',
    powerConsumption: data.expansionDetails.equipment?.powerConsumption || 50,
    quantity: data.expansionDetails.equipment?.quantity || 1,
    operatingHours: data.expansionDetails.equipment?.operatingHours || 8,
    timeline: data.expansionDetails.equipment?.timeline || '6 maanden',
    equipmentTypes: data.expansionDetails.equipment?.equipmentTypes || [],
    energyConsumption: data.expansionDetails.equipment?.energyConsumption || 50,
    implementationTiming: data.expansionDetails.equipment?.implementationTiming || '6 maanden'
  });

  const handleNext = () => {
    updateExpansionDetails?.('equipment', equipmentData);
    nextStep();
  };

  const handleSkip = () => {
    console.log('Skipping new equipment details');
    skipSubScreen?.();
  };

  const equipmentTypes = [
    'Productie machines',
    'IT/Server apparatuur',
    'Klimaatinstallaties',
    'Lichtinstallaties',
    'Laadpalen',
    'Overige apparatuur'
  ];

  const timelines = ['3 maanden', '6 maanden', '1 jaar', '1-2 jaar', '2+ jaar'];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb with logo */}
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-gray-600">
          <span>Expansieplannen</span> <span className="mx-2">{'>'}‍</span> <span className="text-energy-blue font-medium">Nieuwe apparatuur</span>
        </div>
        <Logo className="h-6 object-contain" />
      </div>

      <div className="text-center mb-8">
        <Zap className="w-12 h-12 text-energy-blue mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-energy-green mb-2">
          Details nieuwe apparatuur
        </h2>
        <p className="text-gray-600">
          Specificeer de nieuwe apparatuur voor een nauwkeurige energieanalyse
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {/* Equipment Type */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Type apparatuur <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {equipmentTypes.map((type) => (
              <button
                key={type}
                onClick={() => setEquipmentData(prev => ({ ...prev, equipmentType: type }))}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  equipmentData.equipmentType === type
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Power Consumption */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Vermogen (kW)
          </label>
          <div className="space-y-2">
            <Input
              type="number"
              value={equipmentData.powerConsumption}
              onChange={(e) => setEquipmentData(prev => ({ ...prev, powerConsumption: parseInt(e.target.value) || 0 }))}
              min="1"
              max="1000"
              className="w-full"
            />
            <div className="text-xs text-gray-500">
              Huidige waarde: {equipmentData.powerConsumption} kW
            </div>
          </div>
        </div>

        {/* Quantity */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Aantal
          </label>
          <div className="space-y-2">
            <Input
              type="number"
              value={equipmentData.quantity}
              onChange={(e) => setEquipmentData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
              min="1"
              max="1000"
              className="w-full"
            />
            <div className="text-xs text-gray-500">
              Huidige waarde: {equipmentData.quantity}
            </div>
          </div>
        </div>

        {/* Operating Hours */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Draaiuren per dag
          </label>
          <div className="space-y-2">
            <Input
              type="number"
              value={equipmentData.operatingHours}
              onChange={(e) => setEquipmentData(prev => ({ ...prev, operatingHours: parseInt(e.target.value) || 8 }))}
              min="1"
              max="24"
              className="w-full"
            />
            <div className="text-xs text-gray-500">
              Huidige waarde: {equipmentData.operatingHours} uur
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Geplande implementatie
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {timelines.map((timeline) => (
              <button
                key={timeline}
                onClick={() => setEquipmentData(prev => ({ ...prev, timeline }))}
                className={`p-3 rounded-lg border text-sm transition-all ${
                  equipmentData.timeline === timeline
                    ? 'border-energy-blue bg-energy-blue/10 text-energy-blue font-medium' 
                    : 'border-gray-200 hover:border-energy-blue/50'
                }`}
              >
                {timeline}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          className="border-gray-300"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug
        </Button>

        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleSkip}
            className="border-gray-400 text-gray-600 hover:bg-gray-50"
          >
            <SkipForward className="w-4 h-4 mr-1" /> Overslaan
          </Button>
          
          <Button 
            onClick={handleNext}
            disabled={!equipmentData.equipmentType}
            className="bg-[#014336] text-white hover:bg-[#014336]/90"
          >
            Volgende <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};
